/**
 * Reward Composable
 * 管理獎勵相關功能
 * 原始來源: cuora/src/machine/reward.js
 *
 * 架構：
 * - 狀態管理：使用 Pinia store (stores/rewards/reward.js)
 * - 業務邏輯：保留在 composable 中
 */

import { storeToRefs } from 'pinia'
import { nextTick, onBeforeUnmount, getCurrentInstance } from 'vue'

import { activityApi, paymentApi } from '@/api'
import { useMainStore } from '@/stores/main'
import { useRewardStore } from '@/stores/rewards/reward'

const INBOX_TAB_TYPE_BY_REWARD_TYPE = {
  POINTS: 'DIAMOND',
  GAME: 'FREE_GAME',
}

export const useReward = () => {
  const nuxtApp = useNuxtApp()
  const { $request, $i18n } = nuxtApp
  const { appSecret } = useRuntimeConfig().public
  const { isLoggedIn } = useAuthToken()
  const { setLoading, setModal, setModalParams } = useMainStore()

  const store = useRewardStore()
  const {
    otherRewardList,
    pointsRewardList,
    rewardList,
    activityNo,
    receiveNo,
    withdrawRule,
    rewardType,
    isPlaying,
    callback,
    orderColumn,
    lang,
    receiving,
    pending,
    getPending,
  } = storeToRefs(store)

  // Initialize lang from i18n
  if ($i18n?.locale?.value && !lang.value) {
    store.setLang($i18n.locale.value)
  }

  // SSR-safe: 使用 nuxtApp context 存儲 timer，避免記憶體洩漏
  if (!nuxtApp._rewardContext) {
    nuxtApp._rewardContext = {
      refreshTimer: null,
      // SSR cleanup 方法
      cleanup: () => {
        const ctx = nuxtApp._rewardContext
        if (ctx?.refreshTimer) {
          clearInterval(ctx.refreshTimer)
          ctx.refreshTimer = null
        }
      },
    }
  }
  const ctx = nuxtApp._rewardContext

  const fetchRewardList = async () => {
    if (!isLoggedIn.value) {
      return
    }

    try {
      // 並行載入三種獎勵列表
      const [otherRewardRes, pointsRewardRes, rewardRes] = await Promise.all([
        $request.send(
          activityApi.getRewardList({
            rewardType: 'CASH,FREE,PRIZE',
            count: 30,
            appSecret,
            orderColumn: orderColumn.value,
            lang: lang.value,
          })
        ),
        $request.send(
          activityApi.getRewardList({
            rewardType: 'POINTS',
            count: 30,
            appSecret,
            lang: lang.value,
          })
        ),
        $request.send(
          activityApi.getRewardList({
            count: 30,
            appSecret,
            lang: lang.value,
          })
        ),
      ])

      store.setOtherRewardList(otherRewardRes.data || [])
      store.setPointsRewardList(pointsRewardRes.data || [])
      store.setRewardList(rewardRes.data || [])

      return {
        otherRewardList: otherRewardList.value,
        pointsRewardList: pointsRewardList.value,
        rewardList: rewardList.value,
      }
    } catch (error) {
      console.error('[useReward] fetchRewardList error:', error)
      throw error
    }
  }

  const fetchOtherRewardList = async (newOrderColumn = 'createTime') => {
    store.setOrderColumn(newOrderColumn)

    try {
      const response = await $request.send(
        activityApi.getRewardList({
          rewardType: 'CASH,FREE,PRIZE',
          count: 30,
          appSecret,
          orderColumn: newOrderColumn,
          lang: lang.value,
        })
      )

      const data = response.data || response
      store.setOtherRewardList(data)

      return otherRewardList.value
    } catch (error) {
      console.error('[useReward] fetchOtherRewardList error:', error)
      throw error
    }
  }

  const withdrawData = ref({})
  const fetchWithdrawRulePreview = async ({ activityNo, receiveNo }) => {
    setLoading({ inbox: true })
    store.setActivityNo(activityNo)
    store.setReceiveNo(receiveNo)

    try {
      const response = await $request.send(
        paymentApi.getWithdrawRulePreview({
          applyType: 'activity',
          applyNo: receiveNo,
          amount: null,
        })
      )

      // 確保 response.data 不存在時也有預設物件，避免下游拿到 undefined
      const data = response.data || response || {}
      withdrawData.value = data
      store.setWithdrawRule(data)
      store.setPending(true)
    } catch (error) {
      console.error('[useReward] fetchWithdrawRulePreview error:', error)
    } finally {
      setLoading({ inbox: false })
    }
  }

  const back = () => {
    store.resetPendingState()
  }

  const openInboxForRewardType = async (type) => {
    setModal(null)
    await nextTick()
    const tabType = INBOX_TAB_TYPE_BY_REWARD_TYPE[type]
    if (tabType) setModalParams({ tabType })
    setModal('ModalInbox')
  }

  const receiveReward = async ({
    activityNo: newActivityNo,
    receiveNo: newReceiveNo,
    attended,
    callback: rewardCallback,
    rewardType: newRewardType,
  }) => {
    store.setReceiving(true)
    store.setCallback(rewardCallback || null)
    store.setRewardType(newRewardType || null)

    try {
      const validateReceiveNo = Number(newReceiveNo) > 0 ? Number(newReceiveNo) : undefined

      // 重置 pending 狀態
      store.setPending(false)
      setLoading({ inbox: true })

      const response = await $request.send(
        activityApi.getReward({
          activityNo: newActivityNo,
          receiveNo: validateReceiveNo,
          appSecret,
          attended,
        })
      )

      const data = response.data || response

      // 刷新獎勵列表
      await fetchRewardList()

      // 如果不是 PRIZE 類型，執行回調
      if (newRewardType !== 'PRIZE' && rewardCallback) {
        rewardCallback(data)
      }

      return data
    } catch (error) {
      store.setPending(false)
      console.error('[useReward] receiveReward error:', error)
      throw error
    } finally {
      store.setReceiving(false)
      setLoading({ inbox: false })
    }
  }

  const receiveAllPoints = async (callbackOrOptions) => {
    store.setReceiving(true)

    // Support both old signature (callback) and new signature ({ callback })
    const rewardCallback = typeof callbackOrOptions === 'function' ? callbackOrOptions : callbackOrOptions?.callback

    store.setCallback(rewardCallback || null)

    try {
      const response = await $request.send(
        activityApi.collect({
          collectType: 'collectPoints',
          appSecret,
        })
      )

      const data = response.data || response

      // 刷新獎勵列表
      await fetchRewardList()

      // 執行回調
      if (rewardCallback) {
        rewardCallback(data)
      }

      return data
    } catch (error) {
      console.error('[useReward] receiveAllPoints error:', error)
      throw error
    } finally {
      store.setReceiving(false)
    }
  }

  const setIsPlaying = (newIsPlaying) => {
    store.setIsPlaying(newIsPlaying)
  }

  const setLang = (locale) => {
    store.setLang(locale || lang.value || 'en-US')
  }

  const refresh = async () => {
    await fetchRewardList()
  }

  const startAutoRefresh = () => {
    if (!import.meta.client) return

    stopAutoRefresh()

    ctx.refreshTimer = setInterval(() => {
      // 如果不在遊戲中，才自動刷新
      if (!isPlaying.value) {
        // 背景輪詢用：吞掉 rejection，避免 unhandledrejection 上報 Sentry
        // fetchRewardList 內已 console.error；真實錯誤已於 request 層上報，失敗等下一輪（20s）自動重試
        fetchRewardList().catch(() => {})
      }
    }, 20000)
  }

  const stopAutoRefresh = () => {
    if (!import.meta.client) return

    if (ctx.refreshTimer) {
      clearInterval(ctx.refreshTimer)
      ctx.refreshTimer = null
    }
  }

  // 元件卸載時清理 timer，避免記憶體洩漏
  if (getCurrentInstance()) {
    onBeforeUnmount(() => {
      stopAutoRefresh()
    })
  }

  return {
    otherRewardList,
    pointsRewardList,
    rewardList,
    activityNo,
    receiveNo,
    withdrawRule,
    rewardType,
    isPlaying,
    callback,
    orderColumn,
    lang,
    receiving,
    pending,
    getPending,
    withdrawData,
    fetchRewardList,
    fetchOtherRewardList,
    fetchWithdrawRulePreview,
    receiveReward,
    receiveAllPoints,
    setIsPlaying,
    setLang,
    refresh,
    back,
    openInboxForRewardType,
    startAutoRefresh,
    stopAutoRefresh,
  }
}
